import { Component, OnInit, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IimAutocompleteFilterable, IimAutocompleteModule, IimCardModule } from '@iim-angular/components';
import { TranslateModule } from '@ngx-translate/core';
import { BaseSiteLayoutModule } from '@lib/util';
import { OrderOnBehalfTargetUser, OrderOnBehalfTargetUserDataService } from '@lib/shared/basket-service';

/** Dialog input: the basket's currently selected company code, if any (EPROC-11237). */
export class SelectOrderOnBehalfTargetDialogData {
    constructor(public readonly companyCodeId: string | null) {}
}

/**
 * Wraps an {@link OrderOnBehalfTargetUser} for local (client-side) filtering in the autocomplete —
 * the candidate list is bounded to one tenant, so a server round-trip per keystroke isn't needed.
 */
class OrderOnBehalfTargetUserFilterable implements IimAutocompleteFilterable<OrderOnBehalfTargetUserFilterable> {
    constructor(private readonly targetUser: OrderOnBehalfTargetUser) {}

    public matches(value: string | IimAutocompleteFilterable<OrderOnBehalfTargetUserFilterable>): boolean {
        if (value instanceof OrderOnBehalfTargetUserFilterable) {
            return this.equals(value);
        }

        const valueLowerCase = String(value).toLowerCase();

        return (
            this.targetUser.displayName.toLowerCase().includes(valueLowerCase) ||
            (this.targetUser.login ?? '').toLowerCase().includes(valueLowerCase)
        );
    }

    public get value(): OrderOnBehalfTargetUserFilterable {
        return this;
    }

    public get valueDisplay(): string {
        return this.targetUser.departmentName
            ? `${this.targetUser.displayName} (${this.targetUser.departmentName})`
            : this.targetUser.displayName;
    }

    public equals(identifier: string | number | IimAutocompleteFilterable<OrderOnBehalfTargetUserFilterable>): boolean {
        if (identifier instanceof OrderOnBehalfTargetUserFilterable) {
            return identifier.targetUser.id === this.targetUser.id;
        }

        if (identifier !== undefined) {
            return identifier.toString() === this.targetUser.id;
        }

        return false;
    }

    public get user(): OrderOnBehalfTargetUser {
        return this.targetUser;
    }
}

export class SelectOrderOnBehalfTargetDialogResult {
    constructor(public readonly targetUser: OrderOnBehalfTargetUser) {}
}

@Component({
    selector: 'select-order-on-behalf-target-dialog',
    standalone: true,
    imports: [MatDialogModule, IimCardModule, TranslateModule, BaseSiteLayoutModule, IimAutocompleteModule],
    templateUrl: './select-order-on-behalf-target-dialog.component.html',
})
export class SelectOrderOnBehalfTargetDialogComponent implements OnInit {
    private readonly dialogRef = inject<
        MatDialogRef<SelectOrderOnBehalfTargetDialogComponent, SelectOrderOnBehalfTargetDialogResult>
    >(MatDialogRef);
    private readonly targetUserDataService = inject(OrderOnBehalfTargetUserDataService);
    private readonly data = inject<SelectOrderOnBehalfTargetDialogData>(MAT_DIALOG_DATA, { optional: true });

    public readonly loading = signal(true);
    public readonly loadFailed = signal(false);
    public readonly candidates = signal<OrderOnBehalfTargetUserFilterable[]>([]);
    public readonly selected = signal<OrderOnBehalfTargetUser | null>(null);

    public ngOnInit(): void {
        this.targetUserDataService.listTargetUsers(this.data?.companyCodeId).subscribe({
            next: (users) => {
                this.candidates.set(users.map((user) => new OrderOnBehalfTargetUserFilterable(user)));
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.loadFailed.set(true);
            },
        });
    }

    public onOptionSelected(option: IimAutocompleteFilterable<OrderOnBehalfTargetUserFilterable>): void {
        if (option instanceof OrderOnBehalfTargetUserFilterable) {
            this.selected.set(option.user);
        }
    }

    public save(): void {
        const targetUser = this.selected();
        if (!targetUser) {
            return;
        }
        this.dialogRef.close(new SelectOrderOnBehalfTargetDialogResult(targetUser));
    }

    public close(): void {
        this.dialogRef.close();
    }
}
